import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Phone, MessageCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { businessInfo } from '../mockData';

const HeroBannerSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const videoRef = useRef(null);
  const intervalRef = useRef(null);

  const slides = useMemo(() => [
    {
      type: 'image',
      url: 'https://customer-assets.emergentagent.com/job_vista-blinds-decor/artifacts/m7gqnnf3_1.jpeg',
      alt: 'Vista Fashion Window Blinds Showroom in Delhi'
    },
    {
      type: 'image',
      url: 'https://customer-assets.emergentagent.com/job_vista-blinds-decor/artifacts/x4cuv9j3_WhatsApp%20Image%202026-02-04%20at%205.13.53%20PM.jpeg',
      alt: 'Curtain Rods and Wooden Flooring Display in East Delhi'
    },
    {
      type: 'image',
      url: 'https://customer-assets.emergentagent.com/job_vista-blinds-decor/artifacts/y3tvik58_WhatsApp%20Image%202026-02-04%20at%205.13.54%20PM.jpeg',
      alt: 'Complete Interior Design Showroom - All Heaven Interior'
    },
    {
      type: 'video',
      url: 'https://customer-assets.emergentagent.com/job_vista-blinds-decor/artifacts/bhbeawiv_WhatsApp%20Video%202026-02-04%20at%205.15.27%20PM.mp4',
      alt: 'Premium Interior Design Services Video'
    }
  ], []);

  const startAutoRotate = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
  }, [slides.length]);

  useEffect(() => {
    startAutoRotate();
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [startAutoRotate]);

  useEffect(() => {
    if (slides[currentSlide].type === 'video' && videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(err => console.log('Video autoplay prevented:', err));
    }
  }, [currentSlide, slides]);

  const goToNextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    startAutoRotate();
  }, [slides.length, startAutoRotate]);

  const goToPreviousSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    startAutoRotate();
  }, [slides.length, startAutoRotate]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    startAutoRotate();
  };

  const handleWhatsAppClick = useCallback(() => {
    const phone = businessInfo.phones[0].replace(/\s+/g, '').replace('+', '');
    const message = encodeURIComponent('Hi, I am interested in your interior design services.');
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  }, []);

  return (
    <section className="relative h-[600px] md:h-[700px] lg:h-[800px] w-full overflow-hidden bg-black">
      {/* Slides */}
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {slide.type === 'image' ? (
            <img
              src={slide.url}
              alt={slide.alt}
              className="w-full h-full object-cover"
              loading={index === 0 ? 'eager' : 'lazy'}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-black">
              {/* Desktop: Center video with blurred sides */}
              <div className="hidden md:flex absolute inset-0 items-center justify-center">
                {/* Blurred background */}
                <div className="absolute inset-0 overflow-hidden">
                  <video
                    className="w-full h-full object-cover scale-110 blur-3xl opacity-30"
                    autoPlay
                    muted
                    loop
                    playsInline
                  >
                    <source src={slide.url} type="video/mp4" />
                  </video>
                </div>
                
                {/* Main centered video */}
                <div className="relative z-10 h-full flex items-center">
                  <video
                    ref={index === currentSlide ? videoRef : null}
                    className="h-full w-auto max-w-none object-contain"
                    autoPlay
                    muted
                    loop
                    playsInline
                  >
                    <source src={slide.url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>

              {/* Mobile: Full screen video */}
              <div className="md:hidden absolute inset-0">
                <video
                  ref={index === currentSlide ? videoRef : null}
                  className="w-full h-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                >
                  <source src={slide.url} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Dark Overlay for Text Readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20 z-10"></div>

      {/* Content Overlay */}
      <div className="absolute inset-0 z-20 flex flex-col justify-end pb-12 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          {/* SEO Optimized Badge */}
          <div className="inline-block bg-amber-600 text-white px-4 py-2 rounded-full text-sm font-medium mb-4 shadow-lg">
            Authorized Vista Fashion Dealer - East Delhi
          </div>

          {/* SEO Optimized H1 */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6 leading-tight drop-shadow-2xl">
            Premium Interior Designer in Delhi & Authorized Vista Fashion Blinds Dealer
          </h1>

          {/* SEO Optimized Sub-heading */}
          <p className="text-lg sm:text-xl md:text-2xl text-white/95 mb-6 md:mb-8 max-w-3xl drop-shadow-lg">
            Custom Interiors, Window Blinds, Curtains, Wooden Flooring & Wallpapers in East Delhi
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-amber-700 text-base md:text-lg px-6 md:px-8 py-5 md:py-6 shadow-2xl transition-all backdrop-blur-sm"
              onClick={handleWhatsAppClick}
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              WhatsApp Us
            </Button>
            <a href={`tel:${businessInfo.phones[0]}`}>
              <Button
                size="lg"
                className="bg-amber-600 hover:bg-amber-700 text-white text-base md:text-lg px-6 md:px-8 py-5 md:py-6 shadow-2xl hover:shadow-xl transition-all"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call Now
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="absolute top-1/2 -translate-y-1/2 left-4 z-30">
        <button
          onClick={goToPreviousSlide}
          className="w-12 h-12 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full flex items-center justify-center transition-all shadow-lg"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>
      </div>

      <div className="absolute top-1/2 -translate-y-1/2 right-4 z-30">
        <button
          onClick={goToNextSlide}
          className="w-12 h-12 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full flex items-center justify-center transition-all shadow-lg"
          aria-label="Next slide"
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </button>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex space-x-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-2 rounded-full transition-all ${
              index === currentSlide
                ? 'w-8 bg-amber-600'
                : 'w-2 bg-white/50 hover:bg-white/70'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroBannerSlider;
